import { useState } from "react";

import { Avatar, Button, type Column, EmptyState, Table } from "@/components/common";
import { Guard } from "@/auth/Guard";
import { useMembers, useRevokeMember } from "@/hooks/useInvitations";
import { extractErrorMessage } from "@/services/axiosConfig";
import type { Member } from "@/types/invitation";
import { ConfirmDialog } from "./ConfirmDialog";
import { EditMemberModal } from "./EditMemberModal";

const SCOPE_LABEL: Record<string, string> = {
  workspace: "Workspace",
  organization: "Organizações",
  client: "Empresas",
};

export function MembersTab({ workspaceId }: { workspaceId: number }) {
  const { data: members = [], isLoading } = useMembers(workspaceId);
  const revoke = useRevokeMember(workspaceId);
  const [pendingRevoke, setPendingRevoke] = useState<Member | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Member | null>(null);

  async function confirmRevoke() {
    if (!pendingRevoke) return;
    setRevokeError(null);
    try {
      await revoke.mutateAsync(pendingRevoke.id);
      setPendingRevoke(null);
    } catch (err) {
      setRevokeError(extractErrorMessage(err));
    }
  }

  const columns: Column<Member>[] = [
    {
      id: "user",
      header: "Membro",
      cell: (m) => (
        <div className="emp-table-name">
          <Avatar name={m.user_name ?? m.user_email_masked} size="md" />
          <div>
            <div className="razao">{m.user_name ?? "—"}</div>
            <div className="fantasia">{m.user_email_masked}</div>
          </div>
        </div>
      ),
    },
    { id: "role", header: "Papel", cell: (m) => <span className="td-small">{m.role_name}</span> },
    {
      id: "scope",
      header: "Escopo",
      cell: (m) => {
        const count = m.scope === "organization" ? m.organization_ids.length : m.client_ids.length;
        return (
          <span className="td-small">
            {SCOPE_LABEL[m.scope] ?? m.scope}
            {m.scope !== "workspace" ? ` (${count})` : ""}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "",
      cell: (m) => (
        <Guard permission="access_grants.write">
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Button variant="ghost" iconLeft="settings" onClick={() => setEditing(m)}>
              Editar
            </Button>
            <Button variant="ghost" iconLeft="x" onClick={() => setPendingRevoke(m)}>
              Revogar
            </Button>
          </div>
        </Guard>
      ),
    },
  ];

  return (
    <>
      <Table
        rows={members}
        columns={columns}
        getKey={(m) => String(m.id)}
        empty={
          <EmptyState
            icon="users"
            title={isLoading ? "Carregando…" : "Nenhum membro"}
            description={isLoading ? undefined : "Convide pessoas na aba Convites."}
          />
        }
      />
      {editing && (
        <EditMemberModal
          workspaceId={workspaceId}
          member={editing}
          onClose={() => setEditing(null)}
        />
      )}
      {pendingRevoke && (
        <ConfirmDialog
          title="Revogar acesso"
          message={`Revogar o acesso de ${pendingRevoke.user_name ?? pendingRevoke.user_email_masked}?`}
          confirmLabel="Revogar"
          loading={revoke.isPending}
          error={revokeError}
          onConfirm={() => void confirmRevoke()}
          onClose={() => {
            setPendingRevoke(null);
            setRevokeError(null);
          }}
        />
      )}
    </>
  );
}
